import type { OperationOrigin } from '@/services/uiActions/routine'
import { RoutineError } from '@/services/uiActions/routine'
import { getApiErrorResponse } from '@/utils/apiErrorResponse'

/**
 * Save plumbing for revision history (WF-2795): the headers a full-content save sends, the one
 * revision number an editor page tracks per resource, and the 409 `revision_conflict` body.
 */

export const IF_MATCH_HEADER = 'If-Match'
export const CHANGE_ORIGIN_HEADER = 'X-Torq-Change-Origin'
export const RESTORED_FROM_REVISION_HEADER = 'X-Torq-Restored-From-Revision'

/** The `code` the server puts on a stale-`If-Match` 409. Name conflicts are 409s too, but without it. */
export const REVISION_CONFLICT_CODE = 'revision_conflict'

export interface RevisionWriteOptions {
  /** The revision the edit is based on, sent as `If-Match`. Omitted by name-only renames. */
  expectedRevisionNo?: number | null
  /** Who drove the save. `assistant` when the Workforce Assistant asked for it; informational only. */
  origin?: OperationOrigin
  /** The revision a restore loaded into the editor, sent once on the Save that persists it. */
  restoredFromRevision?: number | string | null
}

/** The request headers for a revision-aware write. Empty when there is nothing to send. */
export function revisionWriteHeaders(options: RevisionWriteOptions = {}): Record<string, string> {
  const headers: Record<string, string> = {}
  if (typeof options.expectedRevisionNo === 'number' && Number.isInteger(options.expectedRevisionNo) && options.expectedRevisionNo >= 0) {
    headers[IF_MATCH_HEADER] = `"${options.expectedRevisionNo}"`
  }
  if (options.origin) headers[CHANGE_ORIGIN_HEADER] = options.origin
  if (options.restoredFromRevision !== undefined && options.restoredFromRevision !== null && options.restoredFromRevision !== '') {
    headers[RESTORED_FROM_REVISION_HEADER] = String(options.restoredFromRevision)
  }
  return headers
}

/**
 * The one revision number an editor page holds for its resource.
 *
 * `hydrate` sets it from the load the form was filled from (and from a restore draft), so a later
 * background refetch that the form never applied cannot hide someone else's save. `observe` takes
 * the number from every write response, keeping the highest. `observeExemptWrite` does the same for
 * a single-field write sent without If-Match, such as the anonymous-access toggle, so it does not make
 * the next builder Save conflict with itself, without skipping over someone else's save.
 */
export class RevisionTracker {
  private value: number | undefined

  constructor(initial?: number | null) {
    this.hydrate(initial)
  }

  get current(): number | undefined {
    return this.value
  }

  hydrate(revisionNo: number | null | undefined): void {
    this.value = typeof revisionNo === 'number' ? revisionNo : undefined
  }

  observe(revisionNo: number | null | undefined): void {
    if (typeof revisionNo !== 'number') return
    if (this.value === undefined || revisionNo > this.value) this.value = revisionNo
  }

  /**
   * Takes the number from a write that is exempt from If-Match (the anonymous-access toggle), but
   * only when it accounts for that write alone: the same revision (a no-op) or the next one. A
   * larger jump means someone else saved in between; moving past it would let the next builder
   * Save overwrite their revision without a conflict, so the tracker stays put and that Save 409s.
   */
  observeExemptWrite(revisionNo: number | null | undefined): void {
    if (typeof revisionNo !== 'number') return
    // No baseline (the resource predates revision history): nothing to skip over, and the next Save
    // sends no If-Match without one.
    if (this.value === undefined || revisionNo === this.value || revisionNo === this.value + 1) this.value = revisionNo
  }

  reset(): void {
    this.value = undefined
  }
}

/** A stale-`If-Match` 409: someone saved the resource since this editor loaded it. */
export interface RevisionConflict {
  currentRevisionNo: number | null
  lastEditorEmail: string | null
  message?: string
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/** The conflict a failed write carries, or undefined for any other failure (including a duplicate-name 409). */
export function getRevisionConflict(error: unknown): RevisionConflict | undefined {
  if (error instanceof RevisionConflictError) return error.conflict
  const response = getApiErrorResponse(error)
  if (!response || response.status !== 409) return undefined
  const body = response.body
  if (!isRecord(body) || body.code !== REVISION_CONFLICT_CODE) return undefined
  return {
    currentRevisionNo: typeof body.currentRevisionNo === 'number' ? body.currentRevisionNo : null,
    lastEditorEmail: typeof body.lastEditorEmail === 'string' && body.lastEditorEmail ? body.lastEditorEmail : null,
    message: typeof body.message === 'string' ? body.message : undefined,
  }
}

/** What the conflict dialog says. */
export function revisionConflictMessage(conflict: RevisionConflict): string {
  const who = conflict.lastEditorEmail ?? 'someone else'
  const as = conflict.currentRevisionNo !== null ? ` as r${conflict.currentRevisionNo}` : ''
  return `Saved by ${who}${as} since you opened this. Reload to see their changes.`
}

/** A save refused because of a revision conflict, for callers that throw rather than return. */
export class RevisionConflictError extends Error {
  readonly conflict: RevisionConflict

  constructor(conflict: RevisionConflict) {
    super(revisionConflictMessage(conflict))
    this.name = 'RevisionConflictError'
    this.conflict = conflict
  }
}

/** The routine failure for a revision conflict: `conflict`, tagged with the server's code. */
export function revisionConflictRoutineError(conflict: RevisionConflict): RoutineError {
  return new RoutineError('conflict', revisionConflictMessage(conflict), REVISION_CONFLICT_CODE)
}

export function isRevisionConflictRoutineError(error: unknown): boolean {
  return error instanceof RoutineError && error.code === REVISION_CONFLICT_CODE
}
